import { CalendarDate } from "@sp-crm/core";
import { DatePreset } from "components/reports/date-presets";

export interface GoalDateRange {
    start: CalendarDate;
    end: CalendarDate;
}

const msPerDay = 24 * 60 * 60 * 1000;

const dayNumber = (date: CalendarDate): number =>
    Date.UTC(date.year, date.month - 1, date.day) / msPerDay;

const shiftDays = (date: CalendarDate, days: number): CalendarDate => {
    const shifted = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
    return new CalendarDate(
        shifted.getUTCFullYear(),
        shifted.getUTCMonth() + 1,
        shifted.getUTCDate(),
    );
};

const daysInMonth = (year: number, month: number): number =>
    new Date(Date.UTC(year, month, 0)).getUTCDate();

export const monthRangeFor = (year: number, month: number): GoalDateRange => ({
    start: new CalendarDate(year, month, 1),
    end: new CalendarDate(year, month, daysInMonth(year, month)),
});

export const monthRangeForYearMonth = (month: string): GoalDateRange => {
    const [year, monthOfYear] = month.split("-").map(part => parseInt(part, 10));
    return monthRangeFor(year, monthOfYear);
};

const monthIndex = (date: CalendarDate): number => date.year * 12 + (date.month - 1);

const monthRangeAt = (index: number): GoalDateRange =>
    monthRangeFor(Math.floor(index / 12), (index % 12) + 1);

const wholeMonthsEndingNow = (
    today: CalendarDate,
    startOffset: number,
): GoalDateRange => ({
    start: monthRangeAt(monthIndex(today) + startOffset).start,
    end: monthRangeAt(monthIndex(today)).end,
});

const weekday = (date: CalendarDate): number =>
    new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();

const weekRangeFor = (date: CalendarDate): GoalDateRange => {
    const start = shiftDays(date, -weekday(date));
    return { start, end: shiftDays(start, 6) };
};

export const defaultGoalDatePreset = (today: CalendarDate): DatePreset => ({
    label: "This Month",
    getRange: () => wholeMonthsEndingNow(today, 0),
});

export const goalDatePresets = (today: CalendarDate): DatePreset[] => [
    { label: "This Week", getRange: () => weekRangeFor(today) },
    { label: "Last Week", getRange: () => weekRangeFor(shiftDays(today, -7)) },
    defaultGoalDatePreset(today),
    { label: "Last Month", getRange: () => monthRangeAt(monthIndex(today) - 1) },
    { label: "Last 3 Months", getRange: () => wholeMonthsEndingNow(today, -2) },
    {
        label: "Year to Date",
        getRange: () => ({
            start: new CalendarDate(today.year, 1, 1),
            end: monthRangeAt(monthIndex(today)).end,
        }),
    },
];

const yearMonthAt = (index: number): string =>
    `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, "0")}`;

const shortDayLabel = (date: CalendarDate): string =>
    date.dayStart().toLocaleDateString("en-US", { month: "short", day: "numeric" });

export const monthStartLabel = (month: string): string =>
    monthRangeForYearMonth(month).start.dayStart().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });

export const monthSpanLabel = (month: string): string => {
    const range = monthRangeForYearMonth(month);
    return `${shortDayLabel(range.start)} – ${shortDayLabel(range.end)}`;
};

export const monthsInRange = (range: GoalDateRange): string[] => {
    const months: string[] = [];
    for (let index = monthIndex(range.start); index <= monthIndex(range.end); index++) {
        months.push(yearMonthAt(index));
    }
    return months;
};

export const rangeDayCount = (range: GoalDateRange): number =>
    dayNumber(range.end) - dayNumber(range.start) + 1;

export const isWholeMonthRange = (range: GoalDateRange): boolean =>
    range.start.day === 1 &&
    range.end.day === daysInMonth(range.end.year, range.end.month);

export const isSingleWholeMonth = (range: GoalDateRange): boolean =>
    isWholeMonthRange(range) &&
    range.start.year === range.end.year &&
    range.start.month === range.end.month;

export const isRangeInPast = (range: GoalDateRange, today: CalendarDate): boolean =>
    dayNumber(range.end) < dayNumber(today);

export const rangeElapsedFraction = (
    range: GoalDateRange,
    today: CalendarDate,
): number => {
    if (dayNumber(today) >= dayNumber(range.end)) {
        return 1;
    }
    if (dayNumber(today) < dayNumber(range.start)) {
        return 0;
    }
    return (dayNumber(today) - dayNumber(range.start) + 1) / rangeDayCount(range);
};

export const priorPeriod = (range: GoalDateRange): GoalDateRange => {
    if (isWholeMonthRange(range)) {
        const monthsCount = monthIndex(range.end) - monthIndex(range.start) + 1;
        return {
            start: monthRangeAt(monthIndex(range.start) - monthsCount).start,
            end: shiftDays(range.start, -1),
        };
    }
    const length = rangeDayCount(range);
    return { start: shiftDays(range.start, -length), end: shiftDays(range.start, -1) };
};

const monthName = (date: CalendarDate): string =>
    new Date(date.year, date.month - 1, 1).toLocaleDateString("en-US", {
        month: "long",
    });

export const rangeLabel = (range: GoalDateRange): string => {
    if (isSingleWholeMonth(range)) {
        return range.start.toMonthAndYearString();
    }
    if (isWholeMonthRange(range)) {
        const startLabel =
            range.start.year === range.end.year
                ? monthName(range.start)
                : range.start.toMonthAndYearString();
        return `${startLabel} – ${range.end.toMonthAndYearString()}`;
    }
    return `${range.start.humanFriendly()} – ${range.end.humanFriendly()}`;
};
